import React, { useState, useEffect, useRef } from 'react'
import {
  Button,
  Menu,
  MenuItem,
  ListItem,
  ListItemText,
  Box,
  SxProps,
  Theme,
} from '@mui/material'
import { RsmBarIcon } from '@rsmus/myrsm-icons'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { useFeatureFlag } from '../../../utils/hooks/useFeatureFlag'
import { Styles } from '../../../types'
import tokens from '../../../styles/tokens.json'
import { AnalyticEvent } from '../../../analytics/adobe/types'

const styles: Styles = {
  listItem: {
    padding: 0,
  },
  button: {
    display: 'flex',
    alignItems: 'center',
    paddingLeft: '0.75rem',
    paddingRight: '0.75rem',
    height: '100%',
    fontFamily: tokens.type.fontFamilies.preloBook,
    fontSize: '1rem',
    color: tokens.colors.white,
    whiteSpace: 'nowrap',
    textTransform: 'none',
    '&:focus-visible': {
      outline: `.125rem solid ${tokens.colors.white}`,
      boxShadow: `0 0 0 .125rem ${tokens.colors.black}`,
    },
  },
}

const analyticEventType: AnalyticEvent = 'Navigation_Click'

type DropdownItem = {
  label: string
  to: string
  testId?: string
  linkStyle?: SxProps<Theme> | undefined
  isCurrent?: boolean
  menuStyle?: SxProps<Theme>
}

type Props = {
  label: string
  items: DropdownItem[]
  testId: string
  linkStyle?: SxProps<Theme> | undefined
  isCurrent?: boolean
  menuStyle?: SxProps<Theme>
}

const DropdownNavListItem = ({
  label,
  items,
  testId,
  linkStyle,
  isCurrent,
  menuStyle,
}: Props) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const navigate = useNavigate()
  const location = useLocation()
  const firstMenuItemRef = useRef<HTMLAnchorElement>(null)
  const isHomePage = location.pathname === '/'
  const isUIRedesignEnabled = useFeatureFlag('UI_Redesign')

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleItemClick = (to: string) => {
    navigate(to)
    handleClose()
  }

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        const menuItems = document.querySelectorAll(
          `#${testId}-menu [role="menuitem"]`,
        )
        if (menuItems[0]) {
          ;(menuItems[0] as HTMLElement).focus()
        }
      }, 0)
    }
  }, [open])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const menuItems = document.querySelectorAll(
      `#${testId}-menu [role="menuitem"]`,
    )
    const activeItem = (e.target as HTMLElement).closest('[role="menuitem"]')

    const currentIndex = Array.from(menuItems).findIndex(
      (item) => item === activeItem,
    )

    if (currentIndex === -1) return

    switch (e.key) {
      case 'Tab':
        break

      case 'Escape':
        e.preventDefault()
        handleClose()
        document.getElementById(`${testId}-button`)?.focus()
        break

      case 'Enter':
      case ' ':
        if ((e.target as HTMLElement).getAttribute('role') === 'menuitem') {
          ;(e.target as HTMLElement).click()
        }
        break

      case 'ArrowDown':
      case 'Down':
        e.preventDefault()
        if (currentIndex < menuItems.length - 1) {
          ;(menuItems[currentIndex + 1] as HTMLElement)?.focus()
        } else {
          ;(menuItems[0] as HTMLElement)?.focus()
        }
        break

      case 'ArrowUp':
      case 'Up':
        e.preventDefault()
        if (currentIndex > 0) {
          ;(menuItems[currentIndex - 1] as HTMLElement)?.focus()
        } else {
          ;(menuItems[menuItems.length - 1] as HTMLElement)?.focus()
        }
        break

      case 'ArrowRight':
      case 'Right':
        e.preventDefault()
        if (currentIndex < menuItems.length - 1) {
          ;(menuItems[currentIndex + 1] as HTMLElement)?.focus()
        }
        break

      case 'ArrowLeft':
      case 'Left':
        e.preventDefault()
        if (currentIndex > 0) {
          ;(menuItems[currentIndex - 1] as HTMLElement)?.focus()
        }
        break

      default:
        break
    }
  }

  return (
    <ListItem sx={styles.listItem}>
      <Button
        id={`${testId}-button`}
        sx={(styles.button, linkStyle)}
        aria-haspopup="true"
        aria-controls={`${testId}-menu`}
        aria-expanded={open}
        aria-label={`${label}, button, ${open ? 'expanded' : 'collapsed'}`}
        aria-current={isCurrent && !isUIRedesignEnabled ? 'page' : undefined}
        onClick={handleClick}
        data-testid={testId}
        data-analytics-id={testId}
        data-analytic-event={analyticEventType}>
        {label}
        {open ? (
          <KeyboardArrowUpIcon
            aria-hidden="true"
            sx={{ fontSize: '1rem', marginLeft: '0.25rem' }}
          />
        ) : (
          <KeyboardArrowDownIcon
            aria-hidden="true"
            sx={{ fontSize: '1rem', marginLeft: '0.25rem' }}
          />
        )}
      </Button>

      <Menu
        id={`${testId}-menu`}
        aria-labelledby={`${testId}-button`}
        anchorEl={anchorEl}
        open={open}
        tabIndex={0}
        onClose={handleClose}
        disableAutoFocus
        disableEnforceFocus
        disableAutoFocusItem
        slotProps={{
          paper: {
            sx: {
              minWidth: '12.5rem',
              backgroundColor: 'transparent',
              boxShadow: isHomePage
                ? 'none'
                : '0 0.25rem 0.5rem rgba(0,0,0, 0.5)',
              padding: 0,
              border: 'none',
              ...(menuStyle || {}),
            },
          },
          root: { sx: { marginTop: '-0.125rem' } },
        }}
        onKeyDown={handleKeyDown}
        MenuListProps={{
          component: 'ul' as React.ElementType,
          role: 'menu',
          'aria-label': `${label} Links`,
          sx: {
            padding: 0,
            backgroundColor: 'transparent',
            border: 'none',
          },
        }}>
        {!isCurrent && isUIRedesignEnabled && (
          <Box
            sx={{
              width: '100%',
              marginTop: '-0.0125rem',
              paddingBottom: '0.3rem',
            }}
            aria-hidden="true"
            tabIndex={-1}>
            <RsmBarIcon />
          </Box>
        )}
        {items.map((item, index) => (
          <Box component="li" key={item.to} role="none">
            <MenuItem
              ref={index === 0 ? firstMenuItemRef : undefined}
              component={Link}
              to={item.to}
              role="menuitem"
              tabIndex={-1}
              onClick={() => handleItemClick(item.to)}
              data-testid={item.testId}
              aria-current={location.pathname === item.to ? 'page' : undefined}
              sx={{
                fontFamily: tokens.type.fontFamilies.preloBook,
                fontSize: '1rem',
                color: tokens.colors.white,
                paddingY: '0.5rem',
                paddingX: '1rem',
                backgroundColor: isHomePage
                  ? 'transparent'
                  : tokens.colors.black,
                display: 'list-item',
                '&:hover': {
                  backgroundColor: tokens.colors.white,
                  color: tokens.colors.black,
                },
                '&.Mui-focusVisible': {
                  backgroundColor: tokens.colors.white,
                  color: tokens.colors.black,
                  outline: 'none',
                },
              }}>
              <ListItemText>{item.label}</ListItemText>
            </MenuItem>
          </Box>
        ))}
      </Menu>
    </ListItem>
  )
}

export default DropdownNavListItem
